import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import vegaLogo from '../../assets/clientLogos/vega_transparent_logo.webp'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/* Hero visual — chain links + trading chart */
const VegaHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="vega-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#7aa2ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="vega-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#vega-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* Trading chart frame */}
    <rect x="60" y="120" width="340" height="220" rx="14" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.18)" />
    <text x="80" y="148" fontFamily="JetBrains Mono, monospace" fontSize="11" fill="rgba(122,162,255,0.9)">VEGA / USDC</text>
    {/* Candles */}
    {[
      [100, 240, 18, 'up'], [124, 220, 14, 'down'], [148, 230, 22, 'up'],
      [172, 200, 30, 'up'], [196, 220, 18, 'down'], [220, 180, 36, 'up'],
      [244, 196, 22, 'up'], [268, 210, 16, 'down'], [292, 175, 30, 'up'],
      [316, 165, 26, 'up'], [340, 180, 18, 'down'], [364, 160, 22, 'up'],
    ].map(([x, y, h, dir], i) => (
      <rect key={i} x={x} y={y} width="14" height={h} fill={dir === 'up' ? 'url(#vega-grad)' : 'rgba(255,107,107,0.85)'} />
    ))}
    {/* Trend line */}
    <polyline
      points="100,238 124,224 148,232 172,206 196,222 220,184 244,200 268,212 292,178 316,168 340,182 364,162"
      fill="none" stroke="url(#vega-grad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.95"
    />

    {/* Chain links */}
    <g transform="translate(330 80)" stroke="url(#vega-grad)" strokeWidth="3" fill="none">
      <path d="M-12 -6 a8 8 0 0 1 0 12 l-8 0 a8 8 0 0 1 0 -12 z" />
      <path d="M0 -6 a8 8 0 0 1 0 12 l-8 0 a8 8 0 0 1 0 -12 z" />
      <path d="M12 -6 a8 8 0 0 1 0 12 l-8 0 a8 8 0 0 1 0 -12 z" />
    </g>

    {/* Wallet badge */}
    <g transform="translate(110 380)">
      <rect x="-30" y="-16" width="60" height="32" rx="6" fill="rgba(0,212,255,0.10)" stroke="rgba(0,212,255,0.55)" />
      <text x="0" y="5" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize="10" fill="#00d4ff">0xWALLET</text>
    </g>
  </svg>
)

const pressLinks = [
  {
    href: 'https://www.coindesk.com/tech/2023/05/10/crypto-derivatives-protocol-vegas-mainnet-goes-live-for-futures-options-trading',
    src: 'CoinDesk',
    title: "Crypto derivatives protocol Vega's mainnet goes live for futures &amp; options trading",
  },
  {
    href: 'https://panteracapital.com/blog-vega-protocol-alpha-mainnet-revolutionizing-defi-and-decentralized-trading/',
    src: 'Pantera Capital',
    title: 'Vega Protocol Alpha Mainnet — revolutionising DeFi and decentralised trading',
  },
  {
    href: 'https://www.theblock.co/post/231875/vega-protocol-blockchain-markets',
    src: 'The Block',
    title: 'Vega Protocol activates on-chain trading after mainnet launch',
  },
]

const Vega = () => (
  <>
    <SEO
      path="/case-studies/vega"
      title="Vega Protocol — DeFi Trading Platform QA & Smart Contract Testing"
      description="QA Tech led QA, automation and smart-contract testing for Vega Protocol — a DeFi derivatives trading platform on Ethereum, including wallet integrations (MetaMask, Vega Wallet), Synpress smart-contract testing and on-chain validation."
      keywords="Vega Protocol case study, DeFi QA, blockchain QA, smart contract testing, Synpress, MetaMask testing, Cypress blockchain, Ganache testing, ERC20 testing, Web3 QA"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Vega Protocol — DeFi trading platform QA',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: { '@type': 'Organization', name: 'QA Tech', url: 'https://qatechltd.com' },
        about: ['Vega Protocol', 'DeFi', 'Blockchain QA', 'Smart contract testing'],
        url: 'https://qatechltd.com/case-studies/vega',
      }}
    />

    <div className="body cs-body">
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link><span aria-hidden="true">/</span>
            <span>Vega Protocol</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <img src={vegaLogo} alt="Vega Protocol" className="cs-hero__logo" loading="lazy" />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">Vega Protocol</h2>
                <p className="cs-hero__client-sub">DeFi derivatives trading platform &mdash; on Ethereum / Web3</p>
              </div>
            </div>

            <h1 className="cs-hero__title">
              On-chain markets. <span className="cs-hero__title-accent">Tested every block.</span>
            </h1>

            <p className="cs-hero__lede">
              We led QA engineering at Vega Protocol &mdash; a Layer 1 DeFi
              derivatives platform &mdash; building automation across the
              UI, the smart contracts, and the wallet integrations that
              real users rely on. From Cypress + Synpress to Ganache to
              dockerised on-chain test environments, we tested it the way
              attackers would too.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_vega_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <VegaHero />
          </div>
        </div>
      </header>

      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat"><span className="cs-stat__num">DeFi</span><span className="cs-stat__label">derivatives platform on Ethereum</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Delivered</span><span className="cs-stat__label">QA engineering programme</span></div>
          <div className="cs-stat"><span className="cs-stat__num">On-chain</span><span className="cs-stat__label">smart contracts tested via Synpress</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Multi-wallet</span><span className="cs-stat__label">MetaMask, Trust, Vega</span></div>
        </div>
      </section>

      <main className="cs-main">
        <SplitSection index={1} align="left" kicker="The brief" title="Test the trading floor of a brand-new chain.">
          <p>
            Vega is a DeFi trading platform on the Ethereum blockchain
            that lets any user create markets at any point in time. We
            led the QA engineering function &mdash; covering the trading
            UI, the smart contracts, the wallet integrations and the
            decentralised network that powered all of it.
          </p>
        </SplitSection>

        <SplitSection index={2} align="right" kicker="UI automation" title="A trading platform, exercised end-to-end.">
          <p>
            We built the test automation framework in TypeScript /
            JavaScript covering the Vega trading UI &mdash; with
            usability and performance checks woven through the same
            pipeline so the experience didn&rsquo;t regress on speed or
            polish.
          </p>
        </SplitSection>

        <SplitSection index={3} align="left" kicker="Smart contracts" title="Cypress + Synpress, on-chain.">
          <p>
            We created a Cypress framework integrated with Synpress for
            testing smart contracts directly &mdash; running against
            decentralised services in Docker to mimic the real chain
            without depending on external testnets. Ganache provided
            local block-by-block control for the high-stakes flows.
          </p>
          <ul>
            <li>Cypress + Synpress for smart-contract test flows</li>
            <li>Ganache for local block-level testing</li>
            <li>Dockerised decentralised services for repeatable runs</li>
          </ul>
        </SplitSection>

        <SplitSection index={4} align="right" kicker="Wallets &amp; integrations" title="MetaMask, Trust Wallet, Vega Wallet.">
          <p>
            The on-chain experience hangs on the wallet. We built
            automation that interacts with multiple wallets &mdash;
            MetaMask, Trust Wallet, Vega&rsquo;s own wallet &mdash; on
            multiple browsers (Chrome and Firefox extensions both),
            keeping the flows stable across environments.
          </p>
        </SplitSection>

        <SplitSection index={5} align="left" kicker="Capsule + Python" title="Chain functions, scripted.">
          <p>
            We used Python to test chain-specific functions like
            initiating transfers and connecting Vega Wallet, against a
            local <strong>Capsule</strong> environment &mdash; a
            dockerised build of the blockchain plus its smart contracts.
            That gave us a real chain to fire scripts against without
            paying mainnet gas or risking mainnet state.
          </p>
        </SplitSection>

        <SplitSection index={6} align="right" kicker="Visual + accessibility" title="Percy, axe, every PR.">
          <p>
            Percy.io for visual regression, axe for accessibility audits
            &mdash; both wired into the GitHub Actions pipeline so
            nothing merges that breaks the look-and-feel or the
            inclusivity bar.
          </p>
        </SplitSection>

        <SplitSection index={7} align="left" kicker="Community &amp; SDLC" title="Beyond testing.">
          <p>
            Active across the whole SDLC &mdash; requirements gathering,
            planning, triaging, releasing &mdash; and helped moderate
            community Q&amp;As on the Vega Discord during launch
            cycles. ERC-20 / Solidity exposure throughout.
          </p>
        </SplitSection>

        <StackRibbon
          label="What we delivered"
          items={[
            'QA engineering leadership', 'Manual + automated test balance',
            'UI automation framework', 'Smart-contract test framework (Cypress + Synpress)',
            'Multi-wallet automation', 'On-chain test environments (Capsule, Docker, Ganache)',
            'Visual regression (Percy.io)', 'Accessibility (axe)',
            'CI/CD (GitHub Actions)', 'Test strategy &amp; environments',
            'Solidity / ERC-20 test exposure',
          ]}
        />

        <StackRibbon
          label="Tech &amp; tools"
          items={[
            'TypeScript', 'JavaScript', 'Python',
            'Cypress', 'Synpress', 'Percy.io', 'axe',
            'Solidity', 'ERC-20',
            'Ganache', 'Docker',
            'MetaMask', 'Trust Wallet', 'Vega Wallet',
            'GitHub Actions',
          ]}
        />

        <section className="cs-press" aria-label="Press and coverage">
          <div className="cs-press__inner">
            <span className="sp-stack__label">Press &amp; coverage</span>
            <p className="cs-press__lead">
              Selected coverage of Vega Protocol&rsquo;s mainnet and
              market launches.
            </p>
            <ul className="cs-press__list">
              {pressLinks.map((p) => (
                <li key={p.href}>
                  <a href={p.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackOutboundClick(p.href, p.title, 'case_study_vega_press')}>
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. QA Tech is independent and
              not affiliated with Vega Protocol.
            </p>
          </div>
        </section>
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Web3 launch on the horizon?</h2>
          <p>If you&rsquo;re shipping smart contracts and the UI in front of them, we&rsquo;ve done it.</p>
        </div>
      </section>

      <div id="cs-contact"><ContactForm /></div>
    </div>
  </>
)

export default Vega
